/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {getPermittedType} from '@ion/applications/src/pages/admin/notifications/NotificationUtils';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {EntityStatus, Feature, GenericObject} from '@ion/core/src/types/entities';
import {useNotificationTypes} from '@ion/query/src/hooks/useNotificationTypes';
import {FormTextField} from '../components/forms/FormTextField';
import {createOptions, Options} from '../components/menus/Dropdown';
import {toggleThemeStyle} from '../theme/helpers';
import {dlsAlt, dlsColor} from '../theme/theme';
import useThemeContext from '../theme/useThemeContext';

// defines the notification type chooser prop definitions ...
export type NotificationTypeChooserProps = {
  isCtcAdmin?: boolean;
  typesToOmit?: string[];
  accountId: number;
  appKey?: string,
  selectedValue?: GenericObject;
  onChange?: (notificationType: GenericObject) => void;
  readOnly?: boolean;
  required?: boolean;
  hasError?: boolean;
  classes?: string;
  name?: string;
  excludedItems?: string[];
  defaultValue?: string;
  defaultLabel?: boolean;
  enabledAlertTypes?: string;
  Features?: Feature;
};

// defines the styles used by notification type chooser ...
const noPadAndMargin = {
  padding : '0px !important',
  margin  : '0px !important'
};
export const useStyles = makeStyles(theme => ({
  root : {
    ...noPadAndMargin
  },
  listbox : {
    ...noPadAndMargin
  },
  popper : {
    zIndex : 4000
  },
  paper : {
    padding : '0px !important'
  },
  option : {
    padding       : '17px !important',
    ...dlsAlt('components', 'dropdown'),
    color         : dlsColor('grey3'),
    textTransform : ({upperCase}: { upperCase: boolean }) => (
      upperCase ? 'uppercase' : 'none'
    ),
    letterSpacing : ({upperCase}: { upperCase: boolean }) => (
      upperCase ? dlsAlt('components', 'dropdown').letterSpacing : 'normal'
    ),
    '&[data-focus="true"]' : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
    }
  },
  endAdornment : {
    paddingRight : '7px !important'
  },
  input : {
    // MUI's own outlined-Autocomplete default (`.MuiAutocomplete-inputRoot
    // [class*="MuiOutlinedInput-root"] .MuiAutocomplete-input`) sets a
    // `9.5px 4px` padding, current theme only - paddingTop/Bottom (rather
    // than a blanket `padding` shorthand) so this doesn't depend on
    // declaration order against paddingLeft/Right below ...
    paddingTop    : toggleThemeStyle(undefined, '0px !important'),
    paddingBottom : toggleThemeStyle(undefined, '0px !important'),
    paddingLeft   : '7px !important',
    paddingRight  : '3em !important'
  },
  formControl : {
    borderBottom           : ({hasError}: { hasError: boolean;}) => hasError ? 'none' : `1px solid ${dlsColor('grey5')}`,
    '& .MuiInputBase-root' : {
      width        : '100%',
      marginTop    : 0,
      border       : ({hasError}: { hasError: boolean; }) => hasError ? `solid 2px ${dlsColor('caRed')}` : 'none',
      borderBottom : ({hasError}: { hasError: boolean }) => hasError ? '0px' : 'none'
    }
  },
  inputRoot : {
    '&::before' : {
      borderBottom : 'none !important'
    },
    '&.Mui-focused' : {
      '&::before' : {
        borderBottom : `1px solid ${dlsColor('aaBlue')} !important`
      }
    }
  },
  popupIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    }
  },
  clearIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    },
    '& .MuiSvgIcon-root' : {
      width     : '0.65em',
      height    : '0.65em',
      '&:hover' : {
        color : dlsColor('caRed')
      }
    }
  }
}));

/**
 * Provides a utility which filters a list of notificationTypes for the
 * currently selected value.
 */
const getSelectedValue = (
  notificationTypes: GenericObject[],
  selected: string
): GenericObject => {
  return notificationTypes?.find(({name}: GenericObject) => name === selected);
};

/**
* Generates the notificationType chooser dropdown labels and values based on
* the notificationTypes fetched for the selected account or user's default account
* and appKey.
*/
const getOptions = (notificationTypes: GenericObject[], isCtcAdmin: boolean): Options[] => {
  return createOptions({
    items     : notificationTypes,
    valProp   : 'id',
    labelProp : 'name',
    augmentor : (item: GenericObject, option: { value: string; label: string; }) => {
      return {...option, disabled: !isCtcAdmin ? item.status !== EntityStatus.Enabled : false};
    }
  });
};

/**
 * The NotificationTypeChooser component displays a list of notificationTypes
  * based on the provided account and appKey, and allows for selecting a notificationType
    * from the list for subsequent use.
 *
 * ```typescript
 *
 *     import {NotificationTypeChooser} from '@ion/dls/src/editors/';
 *
 *     // filter the list of notificationTypes based on the given asset type and categories ...
 *     <NotificationTypeChooser onChange={setRole}
 *       accountId={selectedAccount}
 *       appKey={appKey}
 *       />
 *
 * ```
      *
 * @accountId        The id of the Account for which notificationTypes are to be loaded.
 * @appKey           The appKey
 * @selectedValue    Selected notificationType value on Editing
 * @onChange         Callback prop which will be invoked with the seleted notificationType.
 * @readOnly         When true, the component will be displayed in read - only mode;
 *                   that is, disabled.Defaults to false.
 * @required         When true, the required indicator will be displayed.
 * @hasError         When true, the validationError indicator will be displayed.
 * @classes          Optional classes to apply to the wrapping element.
 * @name             name for the component that gets passed to the root element.
 */
export const NotificationTypeChooser = ({
  isCtcAdmin,
  accountId,
  appKey,
  selectedValue,
  excludedItems,
  enabledAlertTypes,
  onChange,
  defaultValue,
  readOnly,
  required,
  classes,
  Features,
  name,
  hasError = false,
  defaultLabel = true,
  typesToOmit = []
}: NotificationTypeChooserProps) => {
  const {isCurrent: isCurrentTheme}               = useThemeContext();
  const _classes                                  = useStyles({hasError});
  const [notificationTypes, setNotificationTypes] = useState<GenericObject[]>(null);
  const [isLoading, setIsLoading]                 = useState<boolean>(Boolean(accountId));
  const [selected, setSelected]                   = useState<string>(selectedValue?.name || '');
  const [options, setOptions]                     = useState<Options[]>([]);
  const [open, setOpen]                           = useState(false);
  const hasMounted                                = useHasMounted();

  const {
    results: notificationTypesResponse,
    isLoading: isLoadingNotificationTypes,
    error: errorNotificationTypes
  } = useNotificationTypes({enabledAlertTypes});

  useEffect(() => {
  // manages loading notificationTypes based on current search term ...
    setIsLoading(isLoadingNotificationTypes);
    if (hasMounted !== false) {
      const notificationTypesList = notificationTypesResponse &&
      getPermittedType(notificationTypesResponse, Features, typesToOmit);
      const filteredList = notificationTypesList.filter(notification =>
        !excludedItems?.includes(notification.name));
      setNotificationTypes(filteredList);
      setOptions(getOptions(filteredList, isCtcAdmin));
      setIsLoading(isLoadingNotificationTypes);
      const notificationValue = typeof defaultValue === 'string' ? defaultValue : defaultValue?.name;
      const selectedList = isCtcAdmin ? filteredList : notificationTypesList;
      notificationValue && selectedList.length > 0 &&
        onChange?.(getSelectedValue(selectedList, notificationValue));
    }

    if (errorNotificationTypes) {
      error('Notification Types: Error occurred retrieving Notification Types');
      error(errorNotificationTypes);
    }
  }, [notificationTypesResponse, enabledAlertTypes]);

  useEffect(() => {
    if (defaultValue && !selected) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  // invoked when a textfield change is made, delegating the debounce change handler ...
  function handleFieldChange(e: ChangeEvent<HTMLInputElement>) {
    e?.persist();
  };

  return (
    <div className={classes || _classes.root}>
      <FormControl className={_classes.formControl}>
        <Autocomplete disabled={readOnly || isLoading}
          defaultValue={defaultValue || ''}
          autoComplete={false}
          // a required selection has nothing meaningful to clear it to, so
          // the clear (x) indicator only applies when this chooser is
          // optional (e.g. NotificationFilters.tsx's filter panel usage) -
          // current theme only, legacy's required choosers keep their
          // clear button unchanged ...
          disableClearable={isCurrentTheme && required}
          clearOnBlur={false}
          openText=''
          closeText=''
          clearText={translate('common:editors.shared.clearText')}
          classes={{
            popper         : _classes.popper,
            paper          : _classes.paper,
            popupIndicator : _classes.popupIndicator,
            clearIndicator : _classes.clearIndicator,
            endAdornment   : _classes.endAdornment,
            input          : _classes.input,
            inputRoot      : _classes.inputRoot,
            listbox        : _classes.listbox,
            option         : _classes.option
          }}
          open={open}
          onOpen={function() {
            setOpen(true);
          }}
          onClose={function() {
            setOpen(false);
          }}
          getOptionSelected={function(option, value) {
            return option.value === value.value;
          }}
          getOptionLabel={function(option: any) {
            return option?.label ?? option;
          }}
          onChange={onChange ? function(event, value: Options) {
            const val = value?.label;
            setSelected(val);
            onChange(value ? getSelectedValue(notificationTypes, val) : null);
          } : null}
          options={options}
          loading={isLoading}
          selectOnFocus={false}
          clearOnEscape
          loadingText={translate('common:states.loading')}
          noOptionsText={translate('common:editors.shared.noOptionsText')}
          onInputChange={handleFieldChange}
          renderInput={function(params) {
            return (
              <FormTextField {...params}
                autoComplete='off'
                required={required}
                fieldLabel={defaultLabel ? translate('common:notificationType.label') : ''}
                placeholder={translate('common:notificationType.placeholder')}
                name={translate('common:features.notificationTypes.name')}
                error={hasError}
                helperText={hasError && required ? translate('common:editors.shared.required') : ''}
                InputProps={{
                  ...params.InputProps
                }} />
            );
          }} />
      </FormControl>
    </div>
  );
};

export default NotificationTypeChooser;
